import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
} from "@mui/material";
import Grid2 from '@mui/material/Grid2';
import { useTheme } from "@mui/material/styles";
import BasePaper from "components/Base/BasePaper";

export interface GenericRecordSummaryProps<T extends { id?: number | string }> {
  record: T | null;
  titleField: keyof T | (keyof T)[];
  descriptionField?: keyof T | (keyof T)[];
  onUpdate: (id: T["id"], field: keyof T, value: any) => Promise<void>;
  loading?: boolean;
  rightContent?: React.ReactNode;
  belowContent?: React.ReactNode;
  editable?: boolean;
  onClose?: () => void;
}

export default function GenericRecordSummary<T extends { id?: number | string }>({
  record,
  titleField,
  descriptionField,
  onUpdate,
  loading = false,
  rightContent,
  belowContent,
  editable = true,
  onClose,
}: GenericRecordSummaryProps<T>) {
  const theme = useTheme();
  const [editingField, setEditingField] = useState<keyof T | null>(null);
  const [savingField, setSavingField] = useState<keyof T | null>(null);
  const [editValues, setEditValues] = useState<Partial<T>>({});
  
  if (!record) return <Typography>Loading record...</Typography>;

  const handleChange = (field: keyof T, value: any) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (field: keyof T) => {
    const newValue = editValues[field];
    if (newValue !== undefined && newValue !== record[field]) {
      try {
        setSavingField(field);
        await onUpdate(record.id!, field, newValue);
      } catch (err) {
        console.error(`Failed to update ${String(field)}:`, err);
      } finally {
        setSavingField(null);
      }
    }
    setEditingField(null);
  };

  const renderEditableText = (
    field: keyof T,
    variant: "h3" | "body1" = "body1",
    multiline = false
  ) => {
    const isEditing = editingField === field;
    const displayValue = (editValues[field] ?? record[field]) as any;

    if (!editable) {
      return (
        <Typography variant={variant} color="text.primary">
          {displayValue}
        </Typography>
      );
    }

    return isEditing ? (
      <TextField
        autoFocus
        variant="standard"
        fullWidth
        multiline={multiline}
        value={displayValue ?? ""}
        onChange={(e) => handleChange(field, e.target.value)}
        onBlur={() => handleSave(field)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !multiline) handleSave(field);
        }}
        sx={{
          "& .MuiInputBase-input": {
            ...(variant === "h3" ? theme.typography.h3 : theme.typography.body1),
            padding: 0,
          },
        }}
      />
    ) : (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          variant={variant}
          onClick={() => setEditingField(field)}
          sx={{
            cursor: "text",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
            borderRadius: 1,
            px: 0.5,
          }}
        >
          {displayValue || <span style={{ color: "#888" }}>Add description here...</span>}
        </Typography>
        {savingField === field && (
          <CircularProgress size={16} sx={{ verticalAlign: "middle" }} />
        )}
      </Box>
    );
  };

  const renderTitle = () => {
    if (Array.isArray(titleField)) {
      return (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {titleField.map((field) => (
            <React.Fragment key={String(field)}>
              {renderEditableText(field, "h3")}
            </React.Fragment>
          ))}
        </Box>
      );
    }
    return renderEditableText(titleField, "h3");
  };

  const renderDescription = () => {
    if (!descriptionField) return null;

    if (Array.isArray(descriptionField)) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          {descriptionField.map((field) => (
            <React.Fragment key={String(field)}>
              {renderEditableText(field, "body1", true)}
            </React.Fragment>
          ))}
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 1 }}>
        {renderEditableText(descriptionField, "body1", true)}
      </Box>
    );
  };

  return (
  <BasePaper onClose={onClose}>
    <Grid2 container>
      <Grid2 size={rightContent ? 9 : 12}>
        {renderTitle()}
        {renderDescription()}

        {belowContent && (
          <Box sx={{ mt: 3 }}>
            {belowContent}
          </Box>
        )}
      </Grid2>

      {rightContent && (
        <Grid2
          size={3}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-start",
            pr: 1.8,
          }}
        >
          {rightContent}
        </Grid2>
      )}
    </Grid2>

    {loading && (
      <Box sx={{ mt: 2 }}>
        <CircularProgress size={18} />
      </Box>
    )}
  </BasePaper>
);

}
